// @ts-strict-ignore
import React, { useContext, useState } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import type { Option } from '@usealloy/component-library';
import {
  Button,
  ButtonVariant,
  Box,
  Checkbox,
  Modal,
  MultiSelect,
  TextArea,
  TooltipProvider,
} from '@usealloy/component-library';

import { UserContext } from '~/dashboard/context';
import { resolutionDecisionReasonOptions, resolveModalTableColumns } from '../../constants';
import { ResolutionModalTableWrapper, StyledResolutionTable } from '../../styles';
import type { MatchData, ResolutionPayload } from '../../types';
import { resolveByService } from './helper';

interface ResolutionModalProps {
  closeModal: () => void;
  handleResolve: (payload: ResolutionPayload) => void;
  selectedMatches: MatchData[];
  service: string;
}

const ResolutionModal = ({
  closeModal,
  handleResolve,
  selectedMatches,
  service,
}: ResolutionModalProps) => {
  const { featureFlags } = useContext(UserContext);
  const watchlistAgentEnabled = Boolean(featureFlags?.llmWatchlistAgent);

  // Pre-populate reason from AI recommendation if feature flag is enabled
  const initialReason = (() => {
    if (!watchlistAgentEnabled) return null;
    const aiRecommendation = selectedMatches[0]?.ai_recommendation;
    if (!aiRecommendation) return null;
    return resolutionDecisionReasonOptions.find(
      option => option.value === aiRecommendation,
    ) || null;
  })();

  const initialNote = watchlistAgentEnabled ? (selectedMatches[0]?.ai_reason || '') : '';

  const [submitting, setSubmitting] = useState(false);
  const [reason, setReason] = useState<Option>(initialReason);
  const [note, setNote] = useState<string>(initialNote);
  const [suppressed, setSuppressed] = useState<boolean>(service === 'PlaidMonitor');

  return (
    <TooltipProvider>
      <Modal
        header="Resolve Matches"
        id="watchlist-review-resolution-modal"
        isOpen
        onClose={() => {
          datadogRum.addAction('watchlistResolve-close');
          closeModal();
        }}
        primaryAction={(
          <Button
            disabled={!reason}
            loading={submitting}
            onClick={() => resolveByService({
              selectedMatches,
              handleResolve,
              note,
              isSuppressed: suppressed,
              reason: reason.value,
              setSubmitting,
            })}
            variant={ButtonVariant.primary}
            data-dd-action-name="alloydd-watchlistResolve-resolve"
          >
            Resolve
          </Button>
        )}
        secondaryAction={(
          <Button
            onClick={closeModal}
            variant={ButtonVariant.secondary}
            data-dd-action-name="alloydd-watchlistResolve-cancel"
          >
            Cancel
          </Button>
        )}
        size="medium"
      >
        <Box gap={4} direction="column" alignItems="flex-start">
          <ResolutionModalTableWrapper alignItems="flex-start">
            <StyledResolutionTable
              columns={resolveModalTableColumns}
              data={selectedMatches}
            />
          </ResolutionModalTableWrapper>
          <MultiSelect
            label="Decision Reason"
            isMulti={false}
            options={resolutionDecisionReasonOptions}
            placeholder="Select a reason to apply to the selected matches"
            required
            selectedOptions={[reason]}
            onChange={(value: Option) => {
              datadogRum.addAction(`watchlistResolve-reasonOption-${value?.label}`);
              setReason(value);
            }}
            data-dd-action-name="alloydd-watchlistResolve-reasonDropdown"
          />
          <TextArea
            label="Note"
            onChange={({ target: { value } }) => { setNote(value); }}
            placeholder="Add a note to these matches"
            value={note}
            data-dd-action-name="alloydd-watchlistResolve-editNote"
          />
          {service !== 'PlaidMonitor' && (
            <Checkbox
              checked={suppressed}
              helpText="Suppressing will prevent future notifications for this match"
              label="Suppress Selected Matches"
              onChange={({ target: { checked } }) => { setSuppressed(checked); }}
              data-dd-action-name="alloydd-watchlistResolve-suppressionToggle"
            />

          )}

        </Box>
      </Modal>
    </TooltipProvider>
  );
};

export default ResolutionModal;
